import React from "react";
import { Button } from "@/components/ui/button";
import {
  PencilSquareIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface EventSessionActionMenuProps {
  sessionUuid: string;
  isCancelled?: boolean;
  onDeleteSession: (uuid: string) => void;
  onCancelSession?: (uuid: string) => void;
  onEditSession?: (uuid: string) => void;
  onDuplicateSession?: (uuid: string) => void;
}

const EventSessionActionMenu: React.FC<EventSessionActionMenuProps> = ({
  sessionUuid,
  isCancelled,
  onDeleteSession,
  onCancelSession,
  onEditSession,
  onDuplicateSession,
}) => {
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex items-center justify-end gap-1">
        {onEditSession && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={(e) => {
                  stopPropagation(e);
                  onEditSession(sessionUuid);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <PencilSquareIcon className="h-4 w-4" />
                <span className="sr-only">Edit session</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit</TooltipContent>
          </Tooltip>
        )}

        {onDuplicateSession && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={(e) => {
                  stopPropagation(e);
                  onDuplicateSession(sessionUuid);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
                <span className="sr-only">Duplicate session</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Duplicate</TooltipContent>
          </Tooltip>
        )}

        {onCancelSession && !isCancelled && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-7 w-7 p-0 text-gray-500 hover:text-orange-600 dark:text-slate-400 dark:hover:text-orange-400"
                onClick={(e) => {
                  stopPropagation(e);
                  onCancelSession(sessionUuid);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <XCircleIcon className="h-4 w-4" />
                <span className="sr-only">Cancel session</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Cancel</TooltipContent>
          </Tooltip>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-7 w-7 p-0 text-gray-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
              onClick={(e) => {
                stopPropagation(e);
                onDeleteSession(sessionUuid);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <TrashIcon className="h-4 w-4" />
              <span className="sr-only">Delete session</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default EventSessionActionMenu;
